import * as React from "react"
import { gql } from "@apollo/client"
import {
  Badge,
  Box,
  Button,
  Center,
  Heading,
  LinkOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react"
import dayjs from "dayjs"

import { ProfilePostFragment, useGetProfilePostsQuery } from "lib/graphql"
import { useMe } from "lib/hooks/useMe"
import { Modal } from "components/Modal"
import PostEditForm from "components/PostEditForm"

import { LinkButton } from "./LinkButton"

const _ = gql`
  fragment ProfilePost on Post {
    id
    title
    description
    categories
    region
    type
    isClosed
    deadline
    userId
    videoUrl
    matchedUsers {
      count
    }
    createdAt
    updatedAt
  }

  query GetProfilePosts($id: String!, $isNotCurrentUser: Boolean!) {
    user(id: $id) {
      id
      posts {
        ...ProfilePost
      }
      commentedOnPosts @skip(if: $isNotCurrentUser) {
        ...ProfilePost
      }
      matchedOnPosts @skip(if: $isNotCurrentUser) {
        ...ProfilePost
      }
    }
  }
`
interface Props {
  userId: string
}

export function ProfilePosts({ userId }: Props) {
  const { me } = useMe()
  const { data, loading, refetch } = useGetProfilePostsQuery({
    variables: { id: userId, isNotCurrentUser: me?.id !== userId },
  })
  const posts = data?.user?.posts
  const commentedOnPosts = data?.user?.commentedOnPosts
  const matchedOnPosts = data?.user?.matchedOnPosts
  const today = new Date().setHours(0, 0, 0, 0)

  if (loading)
    return (
      <Center pt={20}>
        <Spinner />
      </Center>
    )
  return (
    <Box>
      {(!!!posts || posts.length === 0) && (!!!commentedOnPosts || commentedOnPosts?.length === 0) ? (
        <Stack mt={8} spacing={8} align="center">
          <Text color="gray.500">Geen posts</Text>
        </Stack>
      ) : (
        <Stack spacing={8}>
          {posts &&
            posts.filter((p) => !p.isClosed && new Date(p?.deadline).getTime() > new Date(today).getTime())
              .length > 0 && (
              <Stack>
                <Heading color="gray.400" fontSize="2xl" mt={10} mb={2}>
                  Open
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} minH="150px">
                  {posts
                    ?.filter(
                      (p) => !p.isClosed && new Date(p?.deadline).getTime() > new Date(today).getTime(),
                    )
                    .map((post) => (
                      <ProfilePostTile key={post.id} post={post} refetch={refetch} />
                    ))}
                </SimpleGrid>
              </Stack>
            )}

          {posts && posts.filter((p) => p.isClosed).length > 0 && (
            <Stack>
              <Heading color="gray.400" fontSize="2xl" mt={10} mb={2}>
                Gesloten
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} minH="150px">
                {posts
                  ?.filter((p) => p.isClosed)
                  .map((post) => (
                    <ProfilePostTile key={post.id} post={post} refetch={refetch} />
                  ))}
              </SimpleGrid>
            </Stack>
          )}

          {posts &&
            posts.filter((p) => !p.isClosed && new Date(p?.deadline).getTime() <= new Date(today).getTime())
              .length > 0 && (
              <Stack>
                <Heading color="gray.400" fontSize="2xl" mt={10} mb={2}>
                  Deadline voorbij
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} minH="150px">
                  {posts
                    ?.filter(
                      (p) => !p.isClosed && new Date(p?.deadline).getTime() <= new Date(today).getTime(),
                    )
                    .map((post) => (
                      <ProfilePostTile key={post.id} post={post} refetch={refetch} />
                    ))}
                </SimpleGrid>
              </Stack>
            )}

          {commentedOnPosts && commentedOnPosts?.length > 0 && (
            <Stack>
              <Heading color="gray.400" fontSize="2xl" mt={10} mb={2}>
                MIJN REACTIES
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} minH="150px">
                {commentedOnPosts.map((post) => (
                  <ProfilePostTile key={post.id} post={post} refetch={refetch} />
                ))}
              </SimpleGrid>
            </Stack>
          )}

          {matchedOnPosts && matchedOnPosts?.length > 0 && (
            <Stack>
              <Heading color="gray.400" fontSize="2xl" mt={10} mb={2}>
                Matched
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} minH="150px">
                {matchedOnPosts.map((post) => (
                  <ProfilePostTile key={post.id} post={post} refetch={refetch} />
                ))}
              </SimpleGrid>
            </Stack>
          )}
        </Stack>
      )}
      <Stack align="center" pt={10}>
        <LinkButton href="/prikbord" size="md" variant="solid" colorScheme="yellow">
          Prikbord
        </LinkButton>
      </Stack>
    </Box>
  )
}

interface ProfilePostProps {
  key: string
  post: ProfilePostFragment
  refetch: () => void
}

function ProfilePostTile({ post, refetch }: ProfilePostProps) {
  const modalProps = useDisclosure()

  const { me } = useMe()

  const matched = post.matchedUsers.count > 0

  return (
    <Box position="relative">
      <LinkOverlay href={`/prikbord/${post.id}`} textDecoration="none">
        <Box width="100%" p={4} borderRadius="xl" bgColor="gray.700">
          <Stack>
            <VStack align="flex-start">
              <Badge
                bg={post.type === "LOOKING" ? "pink.500" : "orange.500"}
                color="black"
                fontSize={16}
                whiteSpace="nowrap"
                overflow="hidden"
              >
                {post.type === "LOOKING" ? "Ik zoek" : "Ik bied"}
              </Badge>
              {matched && (
                <Badge fontSize={16} bg="blue.500" color="white" whiteSpace="nowrap" overflow="hidden">
                  MIJN MATCHES
                </Badge>
              )}
            </VStack>
          </Stack>

          <Text
            mt={4}
            color="white"
            lineHeight="shorter"
            fontWeight="black"
            fontSize={35}
            textTransform="uppercase"
          >
            {post.title}
          </Text>
          <Text mt={4} fontWeight="bold" color="gray.300">
            Posted: {post.createdAt && dayjs(post.createdAt).format("DD MMMM")}
          </Text>
          <Text mt={0} fontWeight="bold" color="gray.300">
            Deadline: {post.deadline && dayjs(post.deadline).format("DD MMMM")}
          </Text>
        </Box>
      </LinkOverlay>
      {me?.id === post.userId && (
        <Button
          colorScheme="white"
          color="white"
          variant="outline"
          size="sm"
          position="absolute"
          top={4}
          right={4}
          zIndex={10}
          onClick={modalProps.onOpen}
        >
          Edit
        </Button>
      )}

      <Modal title="Edit Post" {...modalProps} size="2xl">
        <PostEditForm post={post} refetchPosts={refetch} closeModal={modalProps.onClose} />
      </Modal>
    </Box>
  )
}
